/**
 * "Top Einheiten nach erwarteter Miete".
 *
 * THE ORDER IS THE BACKEND'S AND IS NEVER RECOMPUTED HERE.
 * `top_units` arrives ranked by `expected_recurring_rent` descending, because
 * the intended reading is "top units by expected rent" — sorting by
 * `expected_total` instead would let a single one-time fee outrank a year of
 * rent. This component slices for display and does not sort.
 *
 * `recurring_rent_change` is matched by `unit_id` on the server, never by rank
 * position: a unit that moved from 2nd to 5th place has not lost revenue, and
 * comparing positions would claim it had.
 *
 * `unassigned_unit` gets its own row whenever it carries tenancies, because
 * without it the visible list quietly adds up to less than the KPI beside it —
 * `Σ top_units + unassigned_unit` is the period total.
 *
 * WHY THE PERCENTAGE MOVED TO THIRD PLACE (UX-1).
 * A bare `+985.1 %` beside a rent figure is unreadable: it does not say percent of
 * WHAT, against WHICH month, or of which quantity. All three answers were already
 * in the payload and unused — `recurring_rent_change.absolute_change`, the
 * response's own `comparison_period` (arriving here as `comparisonLabel`) and the
 * card's own title. So the row now leads with the amount, states the change in
 * francs against the named month, and keeps the percentage as a secondary figure.
 *
 * That reordering is also what makes an extreme value legible without any special
 * case for it: `+985.1 %` explains itself the moment `+CHF 5'084.00 vs. Juli 2026`
 * stands beside it, because the reader can see the comparison month was tiny. No
 * threshold, no clamp, no "neu" label — the backend prorates, and a percentage it
 * declares unavailable stays unavailable.
 *
 * Nothing here computes: no `selected - comparison`, no division. Both figures are
 * the server's, formatted.
 */
import React, { useState } from "react";
import { ArrowDownRight, ArrowRight, ArrowUpRight } from "lucide-react";

import { changeDisplay, fmtMoney, unitDisplayLabel } from "./revenueAnalyticsFormat";

const PREVIEW_COUNT = 5;

const DIRECTION_ICON = { up: ArrowUpRight, down: ArrowDownRight, flat: ArrowRight };
/**
 * Applied to the FRANC change only.
 *
 * The percentage beside it stays muted on purpose: colouring both would say the
 * same thing twice and make the secondary figure compete with the primary one.
 * Direction is carried by the arrow and by the sign inside the amount, so the
 * hue is never the only signal.
 */
const DIRECTION_TEXT = {
  up: "text-emerald-700 dark:text-emerald-400",
  down: "text-red-700 dark:text-red-400",
  flat: "text-muted-foreground",
};

/**
 * The change block: francs against the named comparison month, percentage after.
 *
 * Stacked on a phone and inline from `sm` up — three figures forced onto one
 * narrow line is exactly what made the old badge unreadable.
 */
function UnitChange({ change, comparisonLabel }) {
  const d = changeDisplay(change);
  const Icon = DIRECTION_ICON[d.direction] || ArrowRight;
  return (
    <span className="flex flex-col items-start gap-x-2 sm:flex-row sm:items-baseline">
      <span className={`inline-flex items-center gap-0.5 text-xs ${DIRECTION_TEXT[d.direction]}`}>
        <Icon aria-hidden="true" className="h-3 w-3 shrink-0" />
        {/* The backend's `absolute_change`, formatted — never rebuilt from the
            two amounts beside it. */}
        {d.absoluteText}
        <span className="text-muted-foreground"> vs. {comparisonLabel}</span>
      </span>
      <span className="text-[11px] text-muted-foreground">{d.percentText}</span>
      {/* Names the quantity the change is ABOUT, and explains a missing
          percentage. The figures themselves are visible text and are not
          repeated here. */}
      <span className="sr-only">
        Veränderung der erwarteten Miete
        {d.hasPercent ? "" : ", kein Prozentvergleich möglich"}
      </span>
    </span>
  );
}

export default function RevenueTopUnitsCard({ units, unassignedUnit, comparisonLabel }) {
  const [expanded, setExpanded] = useState(false);
  const all = Array.isArray(units) ? units : [];
  const shown = expanded ? all : all.slice(0, PREVIEW_COUNT);
  const hasMore = all.length > PREVIEW_COUNT;
  const unassignedCount = Number(unassignedUnit?.tenancy_count ?? 0);

  return (
    <div className="flex h-full flex-col rounded-xl border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">
            Top Einheiten nach erwarteter Miete
          </h2>
          {/* States the compared quantity and the month ONCE, visibly — not only
              in a screen-reader string and not only inside each row. */}
          {all.length > 0 ? (
            <p className="mt-0.5 text-xs text-muted-foreground">
              Veränderung der erwarteten Miete gegenüber {comparisonLabel}
            </p>
          ) : null}
        </div>
        {hasMore ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="shrink-0 rounded text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {expanded ? "Weniger anzeigen" : `Alle anzeigen (${all.length})`}
          </button>
        ) : null}
      </div>

      {all.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">
          Für diesen Monat sind keine Einheiten mit erwarteter Miete vorhanden.
        </p>
      ) : (
        <ol className="mt-3 list-none space-y-1 p-0">
          {shown.map((u, i) => (
            <li
              key={u.unit_id}
              className="flex flex-col gap-0.5 border-b border-border/60 py-1.5 text-sm last:border-b-0 sm:flex-row sm:items-center sm:gap-3"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="w-5 shrink-0 text-xs text-muted-foreground">{i + 1}.</span>
                <span className="min-w-0 flex-1 truncate text-foreground">
                  {unitDisplayLabel(u)}
                </span>
              </span>
              {/* Amount first, change second, percentage third — indented on a
                  phone so the block reads as belonging to the unit above it. */}
              <span className="flex flex-col items-start gap-x-3 pl-7 sm:ml-auto sm:shrink-0 sm:flex-row sm:items-baseline sm:pl-0">
                <span className="font-medium text-foreground">
                  {fmtMoney(u.expected_recurring_rent)}
                </span>
                <UnitChange
                  change={u.recurring_rent_change}
                  comparisonLabel={comparisonLabel}
                />
              </span>
            </li>
          ))}
        </ol>
      )}

      {unassignedCount > 0 ? (
        <div className="mt-2 flex items-center gap-2 rounded-lg border border-dashed border-border bg-muted/40 px-2 py-1.5 text-sm">
          <span className="min-w-0 flex-1 text-muted-foreground">
            Ohne Einheit · {unassignedCount}{" "}
            {unassignedCount === 1 ? "Mietverhältnis" : "Mietverhältnisse"}
          </span>
          <span className="shrink-0 font-medium text-foreground">
            {fmtMoney(unassignedUnit?.expected_recurring_rent)}
          </span>
        </div>
      ) : null}
    </div>
  );
}
